<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue';
import { isDate, isString } from 'class-validator';
import cloneDeep from 'lodash/cloneDeep';
import { DateTime } from 'luxon';
import { isNilOrBlankString, t, toDateTime } from '@nova/core';

type DateValue = string | string[];
type ValidationRule = (value: unknown) => boolean | string;

/**
 * Datepicker
 * @displayName Date Picker
 */
const {
  modelValue,
  id,
  disabled = false,
  label,
  displayFieldLabel = true,
  displayFieldIcon = true,
  compactDisplay = false,
  required = false,
  iconMode = false,
  innerIcon = false,
  format = 'MM/dd/yyyy',
  noInputMode = false,
  clearable = true,
  minDate,
  maxDate,
  placeholder,
  customValidationRules = [],
  hasRipple = true,
  error,
  hideDetails = 'auto',
  hideHeader = true,
  fieldDensity = 'comfortable',
  isRange = false,
  externalTrigger = false,
  fieldMinWidth = '150px',
  noMargins = false,
  testId
} = defineProps<{
  modelValue?: DateValue;
  id?: string;
  /** Disable datepicker */
  disabled?: boolean;
  /** Datepicker label */
  label?: string;
  displayFieldLabel?: boolean;
  displayFieldIcon?: boolean;
  compactDisplay?: boolean;
  required?: boolean;
  iconMode?: boolean;
  innerIcon?: boolean;
  outlined?: boolean;
  format?: string;
  /** It shows the selected date on a label instead of a input */
  noInputMode?: boolean;
  clearable?: boolean;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  customValidationRules?: ValidationRule[];
  hasRipple?: boolean;
  error?: boolean;
  /** Set to true to keep the error state but drop the message, e.g. when a parent owns it. */
  hideDetails?: boolean | 'auto';
  hideHeader?: boolean;
  fieldDensity?: 'default' | 'comfortable' | 'compact';
  isRange?: boolean;
  externalTrigger?: boolean;
  fieldMinWidth?: string;
  noMargins?: boolean;
  testId?: string;
}>();

/**
 * The Options API version emitted `update:modelValue` from the calendar setter and
 * `update:model-value` from the watchers. Vue's own emit resolution tries the camelized and
 * hyphenated spellings of a model event before giving up, so both reached a parent's `v-model`
 * either way — the two names were an inconsistency, not two distinct events.
 */
const emit = defineEmits<{
  'update:model-value': [value: DateValue];
}>();

const selectedDates = ref<DateValue>(isRange ? [] : '');
const formattedDates = ref<DateValue>(isRange ? [] : '');
const isActive = ref(false);

/**
 * Format the date from YYYY-MM-DD to MM/DD/YYYY format
 * @public
 */
function formatDate(date: string): string | null {
  if (!date) {
    return null;
  }

  /**
   * toDateTime throws on anything it cannot parse, and this runs from onBeforeMount and from two
   * watchers, so a stored value that is not ISO — legacy free-text data, most plausibly — took
   * the whole component down on render rather than just showing an empty field.
   */
  try {
    return toDateTime(date, { zone: 'utc' }).toFormat(format);
  } catch {
    return null;
  }
}

function formatDates(dates: DateValue | null | undefined): DateValue {
  let formatted: DateValue = isRange ? [] : '';
  if (dates) {
    if (!Array.isArray(dates)) {
      formatted = formatDate(dates) ?? '';
    } else {
      formatted = dates.map(date => formatDate(date) ?? '');
    }
  }

  return formatted;
}

/**
 * Parse date from MM/DD/YYYY to YYYY-MM-DD
 * @public
 */
function parseDate(date: string): string | null {
  date = String(date);
  if (!date) {
    return null;
  }

  return DateTime.fromFormat(date, format, { zone: 'utc' }).toFormat('yyyy-MM-dd');
}

function parseDates(dates: DateValue | null | undefined): DateValue {
  let parsed: DateValue = isRange ? [] : '';
  if (dates) {
    if (!Array.isArray(dates)) {
      parsed = parseDate(dates) ?? '';
    } else {
      parsed = dates.map(date => parseDate(date) ?? '');
    }
  }

  return parsed;
}

function openDatePicker() {
  isActive.value = true;
}

const computedPlaceHolder = computed(() => {
  if (placeholder) {
    return placeholder;
  }
  const now = DateTime.utc();
  let computedPlaceholder = `e.g. ${now.toFormat(format)}`;
  if (isRange) {
    computedPlaceholder += ` - ${now.plus({ days: 10 }).toFormat(format)}`;
  }
  return computedPlaceholder;
});

/** An ISO date string as a calendar-ready Date, or null when it is empty or unparseable. */
function toJsDate(value: unknown): Date | null {
  if (!isString(value) || !value) {
    return null;
  }
  const isoDate = DateTime.fromISO(value as string, { setZone: true }).toISODate();
  return isoDate ? DateTime.fromISO(isoDate).toJSDate() : null;
}

/** A Date or ISO string as an ISO date string, or null when it is neither or is invalid. */
function toIsoDate(value: unknown): string | null {
  const dt = isDate(value)
    ? DateTime.fromJSDate(value as Date)
    : isString(value)
      ? DateTime.fromISO(value as string, { setZone: true })
      : null;
  return dt?.isValid ? (dt.toISODate() as string) : null;
}

const localValue = computed({
  get() {
    /**
     * v-date-picker hands whatever it gets to its date adapter for calendar maths and header
     * formatting, so it has to receive usable Dates or nothing at all. The unguarded chain this
     * replaced turned an empty or unparseable modelValue into `Invalid Date`, and in range mode
     * handed the picker a single invalid value instead of the array `multiple` expects, so a
     * saved range never showed up as selected.
     */
    if (isRange) {
      return Array.isArray(modelValue)
        ? modelValue.map(toJsDate).filter((date): date is Date => date !== null)
        : [];
    }
    if (!modelValue || Array.isArray(modelValue)) {
      return null;
    }
    return toJsDate(modelValue);
  },
  set(newVal: Date | string | (Date | string)[] | null) {
    /**
     * In `multiple` mode v-date-picker sets an array of Dates, which the scalar conversion
     * rejected outright, so picking a range in the calendar emitted nothing at all. Routing it
     * through selectedDates reuses the formatting and emitting the text-input path already does,
     * keeping both views on the same value.
     */
    if (isRange) {
      selectedDates.value = (Array.isArray(newVal) ? newVal : [])
        .map(toIsoDate)
        .filter((date): date is string => date !== null);
      return;
    }
    const isoDate = toIsoDate(newVal);
    if (!isoDate) {
      return;
    }
    emit('update:model-value', isoDate);
    /**
     * Close even when the ISO date matches the previous value — otherwise modelValue may not
     * re-run the watcher and isActive stays true (calendar + scrim block the rest of the form).
     */
    isActive.value = false;
  }
});

const inputValueDisplay = computed({
  get() {
    return Array.isArray(formattedDates.value)
      ? formattedDates.value.join(' - ')
      : formattedDates.value;
  },
  set(value: string) {
    formattedDates.value = value;
  }
});

const validationRules = computed<ValidationRule[]>(() => {
  if (customValidationRules.length > 0) {
    return customValidationRules;
  }
  if (!required) {
    return [];
  }
  /** Mirrors the plugin's requiredRule: Circe and Juno never install it, so a required date
   * there carried no rule at all. */
  const message = label
    ? `${label} ${t('global.basicTerms.required')}`
    : t('global.errors.thisFieldIsRequired');
  return [
    (value: unknown) =>
      (Array.isArray(value) ? value.length > 0 : !isNilOrBlankString(value)) || message
  ];
});

onBeforeMount(() => {
  if (modelValue) {
    formattedDates.value = formatDates(modelValue);
    selectedDates.value = modelValue;
  }
});

watch(selectedDates, val => {
  if (val) {
    let valClone = cloneDeep(val);
    if (!isRange && Array.isArray(valClone)) {
      valClone = valClone.join('');
    }
    formattedDates.value = formatDates(valClone);

    /**
     * Emits input event with new data
     * @event input
     * @property {string} input - new selected date
     */
    emit('update:model-value', valClone);
  } else if (val === '') {
    emit('update:model-value', '');
  }
});

watch(formattedDates, val => {
  if (val) {
    /**
     * Emits input event with new data
     * @event input
     * @property {string} input - new selected date
     */
    emit('update:model-value', selectedDates.value);
  }
});

watch(
  () => modelValue,
  val => {
    formattedDates.value = formatDates(val);
    selectedDates.value = val as DateValue;
    /**
     * Range picks emit after each click, so a v-model parent feeds the first date straight
     * back through here; closing on it shut the dialog before the end date could be chosen.
     */
    if (!isRange || (Array.isArray(val) && val.length >= 2)) {
      isActive.value = false;
    }
  }
);
</script>

<template>
  <div :id="id" :aria-owns="`${id}-date-picker`">
    <v-dialog width="auto" max-width="auto" v-model="isActive">
      <template v-slot:activator>
        <template v-if="iconMode">
          <slot name="icon-trigger" :open-date-picker="openDatePicker">
            <v-btn
              icon
              color="secondary"
              size="small"
              variant="tonal"
              :data-testid="`${testId}-icon-activator`"
              :disabled="disabled"
              :ripple="hasRipple"
              @click="isActive = true">
              <v-icon icon="mdi-calendar" size="16"></v-icon>
            </v-btn>
          </slot>
        </template>
        <div class="d-flex flex-row align-center ga-2" v-else-if="noInputMode">
          <v-btn
            color="secondary"
            size="small"
            variant="tonal"
            :data-testid="`${testId}-icon-activator`"
            :disabled="disabled"
            :ripple="hasRipple"
            @click="isActive = true">
            <span class="d-flex flex-row align-center ga-2">
              <v-icon icon="mdi-calendar" size="16"></v-icon>
              <span class="typography-body-sm" v-if="inputValueDisplay">
                {{ inputValueDisplay }}
              </span>
            </span>
          </v-btn>
        </div>
        <template v-else-if="externalTrigger">
          <slot name="external-trigger" :open-date-picker="openDatePicker"></slot>
        </template>
        <v-text-field
          v-else
          readonly
          color="primary"
          variant="outlined"
          :hide-details="hideDetails"
          v-model="inputValueDisplay"
          :class="{
            required: required,
            'datepicker-with-label': Boolean(label),
            'ml-4': !displayFieldIcon && !noMargins,
            'mt-4': !noMargins,
            'mt-0': noMargins
          }"
          :clearable="clearable"
          :data-testid="testId"
          :density="fieldDensity"
          :disabled="disabled"
          :error="error"
          :label="displayFieldLabel ? label : undefined"
          :min-width="fieldMinWidth || 'auto'"
          :placeholder="computedPlaceHolder"
          :prepend-icon="displayFieldIcon && !innerIcon ? 'mdi-calendar' : ''"
          :prepend-inner-icon="displayFieldIcon && innerIcon ? 'mdi-calendar' : ''"
          :required="required"
          :rules="validationRules"
          @blur="selectedDates = parseDates(formattedDates)"
          @click:clear="isRange ? (selectedDates = []) : (selectedDates = '')"
          @click="isActive = true"></v-text-field>
      </template>

      <template v-slot:default>
        <v-date-picker
          class="date-picker"
          hide-details="auto"
          min-width="13rem"
          v-model="localValue"
          :clearable="clearable"
          :density="compactDisplay ? 'compact' : 'default'"
          :error="error"
          :hide-header="hideHeader"
          :id="`${id}-date-picker`"
          :label="displayFieldLabel ? label : null"
          :max="maxDate"
          :min="minDate"
          :multiple="isRange"
          :prepend-icon="displayFieldIcon && !innerIcon ? 'mdi-calendar' : ''"
          :prepend-inner-icon="innerIcon && displayFieldIcon ? 'mdi-calendar' : null"
          :rules="customValidationRules"></v-date-picker>
      </template>
    </v-dialog>
  </div>
</template>
